import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useTheme } from '@mui/material/styles';

import { api } from '../services/api';
import Layout from '../layout/layout';
import { useToast } from "../components/ToastContext";

function ApprovalSettings() {
  const [open, setOpen] = useState(false);
  const [setClients] = useState([]);
  const [roles, setRoles] = useState([]);
  const [approversByRole, setApproversByRole] = useState({});
  const [selectedClient, setSelectedClient] = useState('');
  const [levels, setLevels] = useState([]);
  const [resourceLevelEnabled, setResourceLevelEnabled] = useState(false);
  const [selectedResource, setSelectedResource] = useState('');
  const [loading, setLoading] = useState(true);
  const [approvals, setApprovals] = useState([]);

  const toast = useToast();
  const theme = useTheme();
  const userId = sessionStorage.getItem('userId');

  const resources = [
    { id: 4193, name: 'AWS EC2 Instance' },
    { id: 124, name: 'Virtual Machine' },
    { id: 3895, name: 'Resource Group' }
  ];

  /* ---------------------- Helper functions (S2004 fix) ---------------------- */

  const getApproverNameById = (roleId, approverId) => {
    const approvers = approversByRole[roleId] || [];
    const approver = approvers.find(app => app.userId === approverId);
    return approver ? approver.name : '';
  };

  const renderApproverNames = (roleId, selectedApprovers) => {
    if (!Array.isArray(selectedApprovers)) return '';
    return selectedApprovers
      .map(id => getApproverNameById(roleId, id))
      .filter(Boolean)
      .join(', ');
  };

  /* -------------------------------------------------------------------------- */

  const fetchApproversByRole = async (roleId) => {
    try {
      const response = await api.get(`/Account/manageUsers/${userId}/${roleId}`);
      setApproversByRole(prev => ({
        ...prev,
        [roleId]: response.data
      }));
    } catch (error) {
      console.error('Error fetching approvers:', error);
    }
  };

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const clientResponse = await api.get(`/Client/GetOrganizationDetailsByUserId/${userId}`);
      setClients(clientResponse.data);

      const roleResponse = await api.get('/Account/userRoles');
      setRoles(roleResponse.data);

      const approvalResponse = await api.get(`/Client/approvalsettings/${userId}`);
      setApprovals(approvalResponse.data);
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, [userId]);

  const handleOpen = () => {
    setOpen(true);
    setLevels([{ id: 1, assignedApprover: [], selectedRole: '' }]);
    setSelectedClient('');
    setResourceLevelEnabled(false);
    setSelectedResource('');
  };

  const handleClose = () => setOpen(false);

  const handleAddLevel = () => {
    setLevels(prev => [
      ...prev,
      { id: prev.length + 1, assignedApprover: [], selectedRole: '' }
    ]);
  };

  const handleRemoveLevel = (id) => {
    setLevels(prev => prev.filter(level => level.id !== id));
  };

  const handleRoleChange = async (levelId, roleId) => {
    setLevels(prev =>
      prev.map(level =>
        level.id === levelId
          ? { ...level, selectedRole: roleId, assignedApprover: [] }
          : level
      )
    );

    if (!approversByRole[roleId]) {
      await fetchApproversByRole(roleId);
    }
  };

  const handleApproverChange = (levelId, approverId) => {
    setLevels(prev =>
      prev.map(level =>
        level.id === levelId
          ? { ...level, assignedApprover: [approverId] }
          : level
      )
    );
  };

  const getAvailableRoles = (currentLevelId) => {
    const selectedRoles = new Set(
      levels
        .filter(level => level.id !== currentLevelId)
        .map(level => level.selectedRole)
    );

    return roles.filter(role => !selectedRoles.has(role.value));
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'Admin': return theme.palette.admin.main;
      case 'SRE': return theme.palette.sre.main;
      case 'FinopsSupport': return theme.palette.finops.main;
      default: return theme.palette.text.primary;
    }
  };

  const handleSaveConfiguration = async () => {
    if (levels.length === 0) {
      toast.error('At least one approval level is required.');
      return;
    }

    const resource = resources.find(r => String(r.id) === String(selectedResource));

    const config = {
      clientId: selectedClient,
      levelCount: levels.length,
      isResourceLevelApproval: resourceLevelEnabled,
      resourceId: resourceLevelEnabled ? resource?.id : 0,
      levelLists: levels.map(level => ({
        orderOfApproval: level.id,
        roleId: level.selectedRole,
        approverId: level.assignedApprover.length ? level.assignedApprover : [0],
        approvercount: level.assignedApprover.length
      }))
    };

    try {
      const response = await api.post(
        'Client/updateApprovalsettings',
        config,
        { params: { userId } }
      );

      if (response.data === true) {
        toast.success('Configuration saved successfully!');
        fetchInitialData();
        handleClose();
      } else {
        toast.error('Unexpected response from server.');
      }
    } catch (error) {
      toast.error(error.message || 'Error saving configuration.');
    }
  };

  return (
    <Layout pageType="sideBar">
      <Box sx={{ p: 3 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" height="80vh">
            <CircularProgress />
          </Box>
        ) : (
          <Paper sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="h6" fontWeight="bold">
                Client Approval Configurations
              </Typography>
              <IconButton onClick={handleOpen}>
                <SettingsIcon />
              </IconButton>
            </Box>

            {approvals.map(approval => (
              <Box key={approval.approvalRequest.approvalRequestId} sx={{ mt: 2 }}>
                <Typography fontWeight="bold">
                  {approval.approvalRequest.clientName}
                </Typography>

                {approval.approvalRequestApprovers.map(approver => (
                  <Box key={approver.approverId} sx={{ p: 2, bgcolor: 'grey.100', mt: 1 }}>
                    <Typography>Level {approver.orderOfApproval}</Typography>
                    <Chip
                      label={approver.roleName}
                      style={{ backgroundColor: getRoleColor(approver.roleName), color: '#fff' }}
                    />
                  </Box>
                ))}
              </Box>
            ))}
          </Paper>
        )}
      </Box>

      {/* --------------------------- Dialog --------------------------- */}

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle>Configure Client Approval</DialogTitle>
        <DialogContent>
          {levels.map(level => (
            <Box key={level.id} display="flex" alignItems="center" mb={2}>
              <Typography sx={{ flex: 1 }}>Level {level.id}</Typography>

              <FormControl sx={{ width: '40%', mr: 1 }}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={level.selectedRole}
                  onChange={(e) => handleRoleChange(level.id, e.target.value)}
                >
                  {getAvailableRoles(level.id).map(role => (
                    <MenuItem key={role.value} value={role.value}>
                      {role.text}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ width: '40%', mr: 1 }}>
                <InputLabel>Assigned Approvers</InputLabel>
                <Select
                  value={level.assignedApprover}
                  onChange={(e) => handleApproverChange(level.id, e.target.value)}
                  renderValue={(selected) =>
                    renderApproverNames(level.selectedRole, selected)
                  }
                >
                  {(approversByRole[level.selectedRole] || []).map(approver => (
                    <MenuItem key={approver.userId} value={approver.userId}>
                      {approver.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <IconButton color="error" onClick={() => handleRemoveLevel(level.id)}>
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}

          <Button startIcon={<AddIcon />} onClick={handleAddLevel}>
            ADD LEVEL
          </Button>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>CANCEL</Button>
          <Button variant="contained" color="success" onClick={handleSaveConfiguration}>
            SAVE CONFIGURATION
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}

export default ApprovalSettings;